/**
 * Database layer — MySQL via local PHP API.
 * Keeps the previous `supabase` / `db` export names so existing imports keep working.
 */
import { mysql as supabase } from './client';

export { supabase };

export const db = {
  async getUsers() {
    return supabase.from('users').select('*').order('created_at', { ascending: false });
  },

  async getUserById(id) {
    return supabase.from('users').select('*').eq('id', id).single();
  },

  async createUser(userData) {
    return supabase.from('users').insert([userData]).select().single();
  },

  async updateUser(id, updates) {
    return supabase.from('users').update(updates).eq('id', id).select().single();
  },

  async deleteUser(id) {
    return supabase.from('users').delete().eq('id', id);
  },

  async getForms() {
    return supabase
      .from('forms')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: false });
  },

  async getFormById(id) {
    return supabase.from('forms').select('*').eq('id', id).single();
  },

  async getFormByType(formType) {
    return supabase
      .from('forms')
      .select('*')
      .eq('form_type', formType)
      .eq('is_active', true)
      .single();
  },

  async createForm(formData) {
    return supabase.from('forms').insert([formData]).select().single();
  },

  async updateForm(id, updates) {
    return supabase.from('forms').update(updates).eq('id', id).select().single();
  },

  async getSubmissions(filters = {}) {
    let query = supabase
      .from('form_submissions')
      .select('*, forms(name, form_type), users(name, email)')
      .order('submitted_at', { ascending: false });

    if (filters.formId) query = query.eq('form_id', filters.formId);
    if (filters.userId) query = query.eq('user_id', filters.userId);
    if (filters.status) query = query.eq('status', filters.status);
    if (filters.dateFrom) query = query.gte('submitted_at', filters.dateFrom);
    if (filters.dateTo) query = query.lte('submitted_at', filters.dateTo);

    return query;
  },

  async getSubmissionById(id) {
    return supabase
      .from('form_submissions')
      .select('*, forms(name, form_type, fields), users(name, email, phone)')
      .eq('id', id)
      .single();
  },

  async createSubmission(submissionData) {
    return supabase.from('form_submissions').insert([submissionData]).select().single();
  },

  async updateSubmission(id, updates) {
    return supabase.from('form_submissions').update(updates).eq('id', id).select().single();
  },

  async getAnnouncements(published = true) {
    let query = supabase
      .from('announcements')
      .select('*, users(name)')
      .order('created_at', { ascending: false });

    if (published) {
      query = query.eq('is_published', true);
    }

    return query;
  },

  async getAnnouncementById(id) {
    return supabase
      .from('announcements')
      .select('*, users(name)')
      .eq('id', id)
      .single();
  },

  async createAnnouncement(announcementData) {
    return supabase.from('announcements').insert([announcementData]).select().single();
  },

  async updateAnnouncement(id, updates) {
    return supabase.from('announcements').update(updates).eq('id', id).select().single();
  },

  async deleteAnnouncement(id) {
    return supabase.from('announcements').delete().eq('id', id);
  },

  async getStaffProfiles() {
    return supabase
      .from('staff_profiles')
      .select('*, users(name, email)')
      .eq('is_visible', true)
      .order('display_order', { ascending: true });
  },

  async getStaffProfileById(id) {
    return supabase
      .from('staff_profiles')
      .select('*, users(name, email, phone)')
      .eq('id', id)
      .single();
  },

  async createStaffProfile(profileData) {
    return supabase.from('staff_profiles').insert([profileData]).select().single();
  },

  async updateStaffProfile(id, updates) {
    return supabase.from('staff_profiles').update(updates).eq('id', id).select().single();
  },

  async getSettings() {
    return supabase.from('site_settings').select('*');
  },

  async getSetting(key) {
    const { data, error } = await supabase
      .from('site_settings')
      .select('value')
      .eq('key', key)
      .single();
    return { data: data?.value, error };
  },

  async updateSetting(key, value, description = '') {
    return supabase
      .from('site_settings')
      .upsert([
        {
          key,
          value,
          description,
          updated_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
        },
      ])
      .select()
      .single();
  },

  async uploadFile() {
    return { data: null, error: { message: 'File storage not configured for MySQL backend' } };
  },

  async getFileUrl() {
    return '';
  },

  async deleteFile() {
    return { data: null, error: { message: 'File storage not configured for MySQL backend' } };
  },
};

export const testConnection = async () => {
  try {
    const { error } = await supabase.from('users').select('*').limit(1);
    if (error) {
      console.error('Database connection test failed:', error);
      return false;
    }
    console.log('MySQL API connection successful');
    return true;
  } catch (error) {
    console.error('Database connection error:', error);
    return false;
  }
};

export default supabase;
